import React from 'react';
import { useTranslation } from 'react-i18next';
import NotApplicableCellRenderer from 'components/NotApplicableCellRenderer';
import { AWAITING_UPDATE_APPROVAL_STATUS } from 'data/eWarrant/eWarrant.adapter';
import {
  EWarrantInterceptStatus, EWarrantInterceptStatusValue, EWarrantStatus
} from 'data/eWarrant/eWarrant.types';
import './e-warrant-status-cell-renderer.scss';

type Props = {
  value?: string
  /**
   * Overrides the displayed text (and title) without affecting the colour, which is still
   * looked up from value — used where the same underlying status reads out under a more
   * specific label, e.g. an AwaitingUpdateApproval e-warrant showing "Pending Update"
   * instead of the Pending every other undecided e-warrant shows. EWarrantView passes this
   * explicitly, since it already knows which decision it is showing.
   */
  label?: string
  /**
   * The row this cell belongs to, when rendered as an ag-grid cell renderer — ag-grid
   * spreads its ICellRendererParams as props, and `data` is how the list grid's Status
   * column tells an AwaitingUpdateApproval row apart from a first-time AwaitingApproval
   * one without an explicit label: both otherwise read out as the same Pending value (see
   * adaptAuthStatus), but only `data.status`, the raw AUTHSTATUS, still says which.
   */
  data?: { status?: string }
};

/**
 * Shared by the Status and Intercept Status columns — the two dictionaries do not
 * overlap, and an in-flight/succeeded/failed provisioning state reads the same as a
 * pending/approved/rejected decision, so they share the colour classes too. A value
 * in neither dictionary still renders, just without a colour.
 */
const statusClassNames: { [key: string]: string } = {
  [EWarrantStatus.Pending]: 'pending',
  [EWarrantStatus.Approved]: 'approved',
  [EWarrantStatus.Rejected]: 'rejected',
  [EWarrantInterceptStatusValue.NotCreated]: 'not-created',
  [EWarrantInterceptStatusValue.CreateUnderway]: 'pending',
  [EWarrantInterceptStatusValue.CreateSuccess]: 'approved',
  [EWarrantInterceptStatusValue.CreateError]: 'rejected',
  [EWarrantInterceptStatusValue.Created]: 'created',
  // What the portal itself writes when it kicks off a generation, before Xcipio has
  // moved the row on to one of the dictionary values above.
  [EWarrantInterceptStatus.InProgress]: 'pending',
};

export default function EWarrantStatusCellRenderer({ value, label, data }: Props): JSX.Element {
  const { t } = useTranslation();

  if (value == null || value === '-' || value === '') {
    return (
      <NotApplicableCellRenderer />
    );
  }

  const inferredLabel = value === EWarrantStatus.Pending && data?.status === AWAITING_UPDATE_APPROVAL_STATUS
    ? t('eWarrant.view.authorisation.pendingUpdate')
    : undefined;

  const className = `e-warrant-status ${statusClassNames[value] ?? ''}`;
  const displayText = label ?? inferredLabel ?? value;

  return (
    <span className="e-warrant-status-cell-renderer">
      <span className={className} title={displayText}>{displayText}</span>
    </span>
  );
}
